import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  tableCellClasses,
  CircularProgress,
  Stack,
  Fab,
  Fade
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCashAndBankReport, downloadCashAndBankReportExcel } from '../../store/reportStore/reportThunk';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import Filters from '../../components/Filters/Filters';
import { toast } from 'react-toastify';
import styled_ from "@emotion/styled";
import { saveAs } from 'file-saver';
import { Utils } from '../../utils/common-utils';
import moment from 'moment';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0,
    backgroundColor: '#fff',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:last-child td, &:last-child th': {},
}));

interface Transaction {
  _id: string;
  date: string;
  dateFormatted: string;
  type: string;
  voucherNumber: string;
  partyName: string;
  category: string;
  paymentMode: string;
  bankAccount: string;
  amount: number;
  tdsAmount: number;
  netAmount: number;
  status: string;
  remarks: string;
  instrumentNumber: string;
  instrumentDate: string;
  debit: number;
  credit: number;
  runningBalance: string;
  balanceType: 'Dr' | 'Cr';
}

interface CashAndBankData {
  transactions: Transaction[];
  summary: {
    totalReceipts: string;
    totalPayments: string;
    totalTDS: string;
    netCashFlow: string;
    closingBalance: string;
    closingBalanceType: 'Dr' | 'Cr';
    totalTransactions: number;
  };
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const CashAndBankReportAllPaymentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const accountId = selectedAccount?._id;

  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [cashAndBankData, setCashAndBankData] = useState<CashAndBankData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Scroll button states
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTop = useRef(0);

  // Fetch Cash and Bank Report
  const fetchReport = (pageNum: number = 1, search: string = searchTerm, append: boolean = false) => {
    if (!accountId || !filters) return;

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    let queryParams = `${filters}&page=${pageNum}&limit=50`;

    // Add search filter
    if (search.trim()) {
      queryParams += `&search=${encodeURIComponent(search.trim())}`;
    }

    dispatch(fetchCashAndBankReport({ accountId, params: queryParams }))
      .unwrap()
      .then((response) => {
        if (response.success) {
          if (append && cashAndBankData) {
            // Append new transactions to existing ones
            setCashAndBankData({
              ...response.data,
              transactions: [...cashAndBankData.transactions, ...response.data.transactions]
            });
          } else {
            setCashAndBankData(response.data);
          }

          // Check if there's more data to load
          setHasMore(response.data.pagination.hasNextPage);
        } else {
          toast.error('Failed to fetch Cash and Bank report');
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch Cash and Bank report');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  };

  // Fetch report when filters change
  useEffect(() => {
    if (filters) {
      setPage(1);
      fetchReport(1, searchTerm, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
    setPage(1);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  // Debounced search - triggers API call 500ms after user stops typing
  useEffect(() => {
    if (!filters) return;

    const timeoutId = setTimeout(() => {
      setPage(1);
      fetchReport(1, searchTerm, false);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleDownloadExcel = () => {
    if (!accountId || !filters) {
      toast.warn('Please select date range first');
      return false;
    }

    let queryParams = `${filters}&isDownloadExcel=true`;

    if (searchTerm.trim()) {
      queryParams += `&search=${encodeURIComponent(searchTerm.trim())}`;
    }

    return dispatch(downloadCashAndBankReportExcel({ accountId, params: queryParams }))
      .unwrap()
      .then((blob) => {
        const fileName = `Cash_And_Bank_Report_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);
      });
  };

  // Handle scroll event
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const currentScrollTop = scrollContainerRef.current.scrollTop;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const clientHeight = scrollContainerRef.current.clientHeight;

    // Determine scroll direction
    if (currentScrollTop > lastScrollTop.current) {
      setScrollDirection('down');
    } else if (currentScrollTop < lastScrollTop.current) {
      setScrollDirection('up');
    }

    lastScrollTop.current = currentScrollTop;

    // Show button when scrolling
    if (currentScrollTop > 100 || currentScrollTop < scrollHeight - clientHeight - 100) {
      setShowScrollButton(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Hide button after 2 seconds of no scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollButton(false);
      }, 2000);
    }

    // Infinite scroll - load more when near bottom
    const scrollBottom = scrollHeight - currentScrollTop - clientHeight;
    if (scrollBottom < 300 && !isLoadingMore && cashAndBankData?.pagination?.hasNextPage) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchReport(nextPage, searchTerm, true);
    }
  };

  // Handle scroll button click
  const handleScrollButtonClick = () => {
    if (!scrollContainerRef.current) return;

    const scrollHeight = scrollContainerRef.current.scrollHeight;

    if (scrollDirection === 'down') {
      // Scroll to bottom
      scrollContainerRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    } else {
      // Scroll to top
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Get transaction type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'RECEIPT':
        return '#059669';
      case 'PAYMENT':
        return '#dc2626';
      case 'EXPENSE':
        return '#f59e0b';
      case 'EXPENSE_PAYMENT':
        return '#d97706';
      case 'SALARY':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  // Get payment mode color
  const getPaymentModeColor = (mode: string) => {
    switch (mode) {
      case 'CASH':
        return '#10b981';
      case 'CHEQUE':
        return '#3b82f6';
      case 'UPI':
        return '#8b5cf6';
      case 'NETBANKING':
        return '#06b6d4';
      case 'NEFT':
      case 'RTGS':
        return '#0891b2';
      default:
        return '#6b7280';
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2, position: 'relative' }} className={'c-card2'}>
          {/* Scrollable content area */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>
            {/* Report Header */}
            <ReusableReportHeader
              reportType="CASH_AND_BANK"
              title="Cash and Bank Report (All Payments)"
              subtitle={(filterData && filterData.periodString) ? filterData.periodString : 'Comprehensive Cash and Bank Transactions'}
              recordCount={cashAndBankData?.summary?.totalTransactions || 0}
              isLoading={isLoading}
              onDownloadPDF={handleDownloadExcel}
              showPrint={false}
              showShare={false}
            />

            {/* Filters and Summary Section */}
            <Grid container px={2} py={1.5} alignItems="center">
              {/* Left side - Date Filter and Search */}
              <Grid item xs={12} md={9}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Filters
                    type="CASH_AND_BANK"
                    filterHandler={filterOnChangeHandler}
                    selectedFilters={filterdOnChangeHandler}
                    hideFilter={false}
                    hidePartySearch={false}
                  />
                  {/* <TextField
                    size="small"
                    placeholder="Search Voucher No., Party Name, Reference..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: '350px' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  /> */}
                </Stack>
              </Grid>

              {/* Right side summary section */}
              <Grid item xs={12} md={3} style={{ textAlign: 'right', paddingLeft: '16px' }}>
                {cashAndBankData?.summary && (
                  <>
                    <Typography style={{ fontSize: 14, fontWeight: 400, marginBottom: '6px' }}>
                      <strong style={{ paddingRight: 10 }}>Total Transactions:</strong>
                      {cashAndBankData.summary.totalTransactions}
                    </Typography>
                    <Typography style={{
                      fontSize: 14,
                      fontWeight: 400,
                      marginBottom: '6px',
                      color: '#059669'
                    }}>
                      <strong style={{ paddingRight: 10 }}>Total Receipts:</strong>
                      {Utils.formatNumber(parseFloat(cashAndBankData.summary.totalReceipts || '0').toFixed(2))}
                    </Typography>
                    <Typography style={{
                      fontSize: 14,
                      fontWeight: 400,
                      marginBottom: '6px',
                      color: '#dc2626'
                    }}>
                      <strong style={{ paddingRight: 10 }}>Total Payments:</strong>
                      {Utils.formatNumber(parseFloat(cashAndBankData.summary.totalPayments || '0').toFixed(2))}
                    </Typography>
                    <Typography style={{
                      fontSize: 14,
                      fontWeight: 400,
                      marginBottom: '6px',
                      color: '#f59e0b'
                    }}>
                      <strong style={{ paddingRight: 10 }}>Total TDS:</strong>
                      {Utils.formatNumber(parseFloat(cashAndBankData.summary.totalTDS || '0').toFixed(2))}
                    </Typography>
                    <Typography style={{
                      fontSize: 14,
                      fontWeight: 400,
                      marginBottom: '6px',
                      color: parseFloat(cashAndBankData.summary.netCashFlow) >= 0 ? '#059669' : '#dc2626'
                    }}>
                      <strong style={{ paddingRight: 10 }}>Net Cash Flow:</strong>
                      {Utils.formatNumber(parseFloat(cashAndBankData.summary.netCashFlow || '0').toFixed(2))}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>

            {!isLoading ? (
              <>
                <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small' aria-label="customized table">
                  <colgroup>
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '6%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Date</StyledTableCell>
                      <StyledTableCell>Type</StyledTableCell>
                      <StyledTableCell>Voucher No.</StyledTableCell>
                      <StyledTableCell>Party Name</StyledTableCell>
                      <StyledTableCell>Category</StyledTableCell>
                      <StyledTableCell>Mode</StyledTableCell>
                      <StyledTableCell>Bank Account</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Amount</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>TDS</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Net Amount</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(cashAndBankData && cashAndBankData.transactions && cashAndBankData.transactions.length > 0) ? cashAndBankData.transactions.map((txn, index) => (
                      <StyledTableRow key={`${txn._id}-${index}`}>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" fontWeight={500}>
                            {txn.dateFormatted}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Chip
                            label={txn.type}
                            size="small"
                            variant="outlined"
                            style={{
                              fontSize: '8pt',
                              height: '22px',
                              borderColor: getTypeColor(txn.type),
                              color: getTypeColor(txn.type)
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell style={{ fontSize: '9pt', fontWeight: '500', verticalAlign: 'middle' }}>
                          {txn.voucherNumber}
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" style={{ fontSize: '9pt' }}>
                            {txn.partyName}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" style={{ fontSize: '8pt', color: '#6b7280' }}>
                            {txn.category}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Chip
                            label={txn.paymentMode}
                            size="small"
                            style={{
                              fontSize: '8pt',
                              height: '20px',
                              backgroundColor: getPaymentModeColor(txn.paymentMode),
                              color: '#fff'
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" style={{ fontSize: '8pt' }}>
                            {txn.bankAccount}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell style={{
                          textAlign: 'right',
                          fontWeight: '500',
                          verticalAlign: 'middle'
                        }}>
                          {Utils.formatNumber(txn.amount.toFixed(2))}
                        </StyledTableCell>
                        <StyledTableCell style={{
                          textAlign: 'right',
                          fontWeight: txn.tdsAmount > 0 ? '600' : 'normal',
                          color: txn.tdsAmount > 0 ? '#f59e0b' : '#757575',
                          verticalAlign: 'middle'
                        }}>
                          {txn.tdsAmount > 0 ? Utils.formatNumber(txn.tdsAmount.toFixed(2)) : ''}
                        </StyledTableCell>
                        <StyledTableCell style={{
                          textAlign: 'right',
                          fontWeight: 'bold',
                          color: txn.type === 'RECEIPT' ? '#059669' : '#dc2626',
                          verticalAlign: 'middle'
                        }}>
                          {Utils.formatNumber(txn.netAmount.toFixed(2))}
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Chip
                            label={txn.status}
                            size="small"
                            style={{
                              fontSize: '7pt',
                              height: '18px',
                              backgroundColor: txn.status === 'PAID' || txn.status === 'RECEIVED' ? '#dcfce7' : '#fff7ed',
                              color: txn.status === 'PAID' || txn.status === 'RECEIVED' ? '#059669' : '#f59e0b'
                            }}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    )) : (
                      <StyledTableRow>
                        <StyledTableCell colSpan={11} style={{ textAlign: 'center' }}>No Data Found</StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Loading More Indicator */}
                {isLoadingMore && (
                  <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={30} color="primary" />
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                      Loading more transactions...
                    </Typography>
                  </Box>
                )}

                {/* No More Data Info */}
                {cashAndBankData && !cashAndBankData.pagination.hasNextPage && cashAndBankData.transactions.length > 0 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing all {cashAndBankData.pagination.totalRecords} transactions
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Grid container p={2}>
                <Grid xs={12} style={{ textAlign: 'center' }}>
                  <CircularProgress color="info" />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Fixed Total Row */}
          {!isLoading && cashAndBankData?.summary && (
            <Box>
              <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small'>
                <colgroup>
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '6%' }} />
                </colgroup>
                <TableBody>
                  <TableRow style={{ backgroundColor: '#f1f5f9' }}>
                    <StyledTableCell colSpan={7} style={{
                      fontWeight: 'bold',
                      fontSize: '11pt',
                      backgroundColor: '#f1f5f9',
                      color: '#1e293b',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5,
                      paddingLeft: '15px !important'
                    }}>
                      TOTAL
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#1e293b',
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5,
                      paddingLeft: '15px !important',
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber((parseFloat(cashAndBankData.summary.totalReceipts) + parseFloat(cashAndBankData.summary.totalPayments)).toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#f59e0b',
                      backgroundColor: '#fff7ed',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      borderLeft: '1px solid #f59e0b',
                      borderRight: '1px solid #f59e0b',
                      borderBottom: '1px solid #f59e0b',
                      padding: 5,
                      paddingLeft: '15px !important',
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber(parseFloat(cashAndBankData.summary.totalTDS || '0').toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: parseFloat(cashAndBankData.summary.netCashFlow) >= 0 ? '#059669' : '#dc2626',
                      backgroundColor: parseFloat(cashAndBankData.summary.netCashFlow) >= 0 ? '#dcfce7' : '#fee2e2',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      borderLeft: `1px solid ${parseFloat(cashAndBankData.summary.netCashFlow) >= 0 ? '#059669' : '#dc2626'}`,
                      borderRight: `1px solid ${parseFloat(cashAndBankData.summary.netCashFlow) >= 0 ? '#059669' : '#dc2626'}`,
                      borderBottom: `1px solid ${parseFloat(cashAndBankData.summary.netCashFlow) >= 0 ? '#059669' : '#dc2626'}`,
                      padding: 5,
                      paddingLeft: '15px !important',
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber(Math.abs(parseFloat(cashAndBankData.summary.netCashFlow || '0')).toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5
                    }}>
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Scroll to Top/Bottom Button */}
          <Fade in={showScrollButton}>
            <Fab
              color="primary"
              size="medium"
              onClick={handleScrollButtonClick}
              sx={{
                position: 'absolute',
                bottom: 46,
                right: 16,
                zIndex: 1000,
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease-in-out'
              }}
              aria-label={scrollDirection === 'down' ? 'scroll to bottom' : 'scroll to top'}
            >
              {scrollDirection === 'down' ? <ArrowDownIcon /> : <ArrowUpIcon />}
            </Fab>
          </Fade>
        </Box>
      </Grid>
    </Grid>
  );
};

export default CashAndBankReportAllPaymentsPage;
